import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getV1CompaniesCompanyIdJobsId,
  getV1CompaniesCompanyIdJobsJobIdTickets,
  getV1CompaniesCompanyIdSettlementsSettlementIdLoads,
} from '@treadinc/horizon-api-spec';
import { noop, uniq } from 'lodash-es';
import { createContext, useCallback, useMemo, useState } from 'react';

import { MAX_API_PAGE_SIZE } from '~constants/consts';
import { Job } from '~hooks/useJob/models';
import { Load, NON_CANCELLED_LOAD_STATES } from '~hooks/useLoad/models';
import { Settlement } from '~hooks/useSettlements/models';
import { Ticket } from '~hooks/useTickets/models';
import { fetchAllApiPages } from '~utils/fetchAllPages';

type SettlementLoadsQueryContextType = {
  addTicket: (ticket: Ticket) => void;
  clearQueryData: () => void;
  companyId: string;
  fetchJobById: (jobId: string) => Promise<Job>;
  idsOfBusyLoads: string[];
  isFetchingJobs?: boolean;
  isFetchingLoads?: boolean;
  isFetchingTickets?: boolean;
  jobsData: Job[];
  loadsData: Load[];
  refreshLoads: () => Promise<void>;
  refreshLoadsAndTickets: () => Promise<void>;
  settlement: Settlement;
  ticketsData: Ticket[];
  toggleLoadsBusyState: (loadsIds: string[]) => void;
  updateStoredJob: (job: Job) => void;
  updateStoredLoad: (load: Load) => void;
  updateStoredTicket: (ticket: Ticket) => void;
};

const SettlementLoadsQueryContext = createContext<SettlementLoadsQueryContextType>({
  addTicket: noop,
  clearQueryData: noop,
  companyId: '',
  fetchJobById: () => Promise.resolve({} as Job),
  idsOfBusyLoads: [],
  jobsData: [],
  loadsData: [],
  refreshLoads: () => Promise.resolve(),
  refreshLoadsAndTickets: () => Promise.resolve(),
  settlement: {} as Settlement,
  ticketsData: [],
  toggleLoadsBusyState: noop,
  updateStoredJob: noop,
  updateStoredLoad: noop,
  updateStoredTicket: noop,
});

interface SettlementLoadsQueryContextProviderProps {
  children: React.ReactNode;
  companyId: string;
  settlement: Settlement;
}

function SettlementLoadsQueryContextProvider({
  children,
  companyId,
  settlement,
}: SettlementLoadsQueryContextProviderProps) {
  const queryPrefix = 'SettlementLoadsQueryContext';
  const loadsQueryKey = `${queryPrefix}_loads`;
  const ticketsQueryKey = `${queryPrefix}_tickets`;

  const queryClient = useQueryClient();

  const [idsOfBusyLoads, setIdsOfBusyLoads] = useState<string[]>([]);

  // Store tickets that get updated via RTU or TicketDrawer
  const [updatedTicketsMap, setUpdatedTicketsMap] = useState<Map<string, Ticket>>(
    new Map(),
  );

  const settlementId = settlement.id ?? '';

  const loadsQuery = useQuery({
    queryKey: [loadsQueryKey, companyId, settlementId],
    enabled: Boolean(settlementId),
    queryFn: ({ signal }) =>
      fetchAllApiPages(
        (query, pageSignal) =>
          getV1CompaniesCompanyIdSettlementsSettlementIdLoads({
            path: { 'company-id': companyId, 'settlement-id': settlementId },
            query: { 'filter[states]': NON_CANCELLED_LOAD_STATES, ...query },
            signal: pageSignal,
          }),
        Load.parse,
        { signal },
      ),
  });

  // Extract unique job IDs from loads to fetch tickets
  const jobIdsFromLoads = useMemo(() => {
    return uniq((loadsQuery.data ?? []).map((load) => load.jobId));
  }, [loadsQuery.data]);

  // Fetch tickets for all jobs associated with the settlement loads
  const ticketsQuery = useQuery({
    queryKey: [ticketsQueryKey, companyId, jobIdsFromLoads],
    enabled: jobIdsFromLoads.length > 0,
    queryFn: async ({ signal }) => {
      const ticketPromises = jobIdsFromLoads.map((jobId) =>
        getV1CompaniesCompanyIdJobsJobIdTickets({
          path: { 'company-id': companyId, 'job-id': jobId },
          query: { 'page[limit]': MAX_API_PAGE_SIZE },
          signal,
        }).then((response) => response.data.data.map((ticket) => Ticket.parse(ticket))),
      );

      const ticketArrays = await Promise.all(ticketPromises);
      return ticketArrays.flat();
    },
  });

  // Combine fetched tickets with RTU-updated tickets
  const ticketsData = useMemo(() => {
    const fetchedTickets = ticketsQuery.data ?? [];
    const updatedTickets = Array.from(updatedTicketsMap.values());

    // Merge: RTU updates take precedence over fetched data
    const ticketMap = new Map<string, Ticket>();
    fetchedTickets.forEach((ticket) => ticketMap.set(ticket.id, ticket));
    updatedTickets.forEach((ticket) => ticketMap.set(ticket.id, ticket));

    return Array.from(ticketMap.values());
  }, [ticketsQuery.data, updatedTicketsMap]);

  // Store fetched jobs for the TicketDrawer
  const [jobsMap, setJobsMap] = useState<Map<string, Job>>(new Map());

  const fetchJobById = useCallback(
    (jobId: string): Promise<Job> => {
      // Check if we already have the job cached
      if (jobsMap.has(jobId)) {
        return Promise.resolve(jobsMap.get(jobId)!);
      }

      return getV1CompaniesCompanyIdJobsId({
        path: { 'company-id': companyId, id: jobId },
      }).then((response) => {
        const job = Job.parse(response.data.data);
        setJobsMap((prev) => new Map(prev).set(jobId, job));
        return job;
      });
    },
    [companyId, jobsMap],
  );

  const updateStoredLoad = useCallback<
    SettlementLoadsQueryContextType['updateStoredLoad']
  >(
    (load) => {
      queryClient.setQueryData(
        [loadsQueryKey, companyId, settlementId],
        (oldLoads?: Load[]) => {
          return oldLoads?.map((oldLoad) => {
            if (oldLoad.id === load.id) {
              return load;
            }
            return oldLoad;
          });
        },
      );
    },
    [companyId, settlementId, queryClient, loadsQueryKey],
  );

  const addTicket = useCallback<SettlementLoadsQueryContextType['addTicket']>(
    (ticket) => {
      setUpdatedTicketsMap((prev) => new Map(prev).set(ticket.id, ticket));
    },
    [],
  );

  const updateStoredTicket = useCallback<
    SettlementLoadsQueryContextType['updateStoredTicket']
  >((ticket) => {
    setUpdatedTicketsMap((prev) => new Map(prev).set(ticket.id, ticket));
  }, []);

  const updateStoredJob = useCallback<SettlementLoadsQueryContextType['updateStoredJob']>(
    (job) => {
      setJobsMap((prev) => new Map(prev).set(job.id, job));
    },
    [],
  );

  const clearQueryData = useCallback(() => {
    queryClient.removeQueries({
      predicate(query) {
        return query.queryKey.some((key) => {
          return String(key).startsWith(queryPrefix);
        });
      },
    });
    setJobsMap(new Map());
    setUpdatedTicketsMap(new Map());
  }, [queryClient]);

  const refreshLoads = useCallback(async () => {
    await loadsQuery.refetch();
  }, [loadsQuery]);

  const refreshLoadsAndTickets = useCallback(async () => {
    await loadsQuery.refetch();
    await ticketsQuery.refetch();
  }, [loadsQuery, ticketsQuery]);

  const loadsData = loadsQuery.data ?? [];
  const isFetchingLoads = loadsQuery.isFetching && !loadsQuery.isRefetching;
  const isFetchingTickets = ticketsQuery.isFetching && !ticketsQuery.isRefetching;

  const jobsData = Array.from(jobsMap.values());
  const isFetchingJobs = false; // Jobs are fetched on-demand

  const toggleLoadsBusyState = useCallback<
    SettlementLoadsQueryContextType['toggleLoadsBusyState']
  >((loadIds: string[]) => {
    setIdsOfBusyLoads((state) => {
      const newState = [...state];

      loadIds.forEach((loadId) => {
        const index = newState.indexOf(loadId);
        const isBusy = index > -1;

        if (isBusy) {
          newState.splice(index, 1);
        } else {
          newState.push(loadId);
        }
      });

      return newState;
    });
  }, []);

  return (
    <SettlementLoadsQueryContext.Provider
      value={{
        addTicket,
        clearQueryData,
        companyId,
        fetchJobById,
        idsOfBusyLoads,
        isFetchingJobs,
        isFetchingLoads,
        isFetchingTickets,
        jobsData,
        loadsData,
        refreshLoads,
        refreshLoadsAndTickets,
        settlement,
        ticketsData,
        toggleLoadsBusyState,
        updateStoredJob,
        updateStoredLoad,
        updateStoredTicket,
      }}
    >
      {children}
    </SettlementLoadsQueryContext.Provider>
  );
}

export default SettlementLoadsQueryContext;
export { SettlementLoadsQueryContextProvider };
